import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { BACKEND_URL } from '../config';

// === FALLBACKS ===
const FALLBACK_VISUAL_THEME = {
    primary_colors: { header: "#FFFFFF", background: "#F8F9FA" },
    secondary_colors: { link: "#0D6EFD", accent: "#0D6EFD" },
    text_colors: { primary: "#212529", secondary: "#6C757D" },
    logo_assets: { primary_light: "/assets/default_logo.svg" },
    banner_assets: {},
    font_definitions: { primary_font: "Inter, sans-serif" },
    component_styles: { button: { border_radius: "4px", background_color: "#0D6EFD" } },
    text_home1: "", text_home2: "", title1: "", title2: "", title3: "",
    desc_text1: "", desc_text2: "", desc_text3: "",
    navbar_text1: "", navbar_text2: "", navbar_text3: "",
    label_input_text: "", label_button_text: ""
};

const FALLBACK_LOCALIZATION = {
    texts: { 
        "home.title": "Benvenuto", 
        "home.subtitle": "Piattaforma Multi-Tenant",
        "navbar.login": "Accedi",
        "navbar.register": "Registrati",
        "navbar.profile": "Profilo",
        "navbar.logout": "Esci"
    },
    language_code: 'it-IT'
};

const FALLBACK_CONFIG = {
    domain_name: 'localhost', 
    project_name: 'TP-Agents', 
    brand_name: 'Default Brand', 
    organization_name: 'Default Organization',
    visual_theme: FALLBACK_VISUAL_THEME, 
    localization: FALLBACK_LOCALIZATION,
    _isFallback: true,
    _fallbackReason: 'domain_not_configured'
};

// Ottimizzazione: definito una sola volta poiché non cambia durante la sessione
const currentDomain = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : 'localhost';

const DomainContext = createContext(null);

export const DomainProvider = ({ children }) => {
    const [domainConfig, setDomainConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shouldShowCourtesyPage, setShouldShowCourtesyPage] = useState(false);

    const fetchDomainConfig = useCallback(async (language = 'it-IT', forcedDomain = null) => {
        const domainName = forcedDomain || currentDomain;
        setLoading(true);
        setError(null);

        try {
            const cacheKey = `domainConfig:${domainName}:${language}`;
            const cachedItem = sessionStorage.getItem(cacheKey);
            // Cache valida per 1 minuto
            if (cachedItem) {
                try {
                    const cached = JSON.parse(cachedItem);
                    if (cached.timestamp > Date.now() - 60_000) {
                        setDomainConfig(cached.config);
                        setShouldShowCourtesyPage(false);
                        setLoading(false);
                        return;
                    }
                } catch { /* ignora cache corrotta */ }
            }

            const apiUrl = `${BACKEND_URL}/api/resolve?domainName=${encodeURIComponent(domainName)}&language=${encodeURIComponent(language)}`;

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10_000); // Timeout 10s

            const response = await fetch(apiUrl, {
                signal: controller.signal,
                // Header aggiunti per coerenza con backend multi-tenant
                headers: {
                    'Accept': 'application/json',
                    'Accept-Language': language,
                    'X-Domain-Name': domainName,
                    'X-Requested-With': 'tp-agents-ui'
                },
                // Nota: niente credentials qui; il resolve non richiede auth
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
                if (response.status === 404) {
                    setShouldShowCourtesyPage(true);
                    setDomainConfig({ ...FALLBACK_CONFIG, domain_name: domainName, _fallbackReason: 'domain_not_found' });
                    setLoading(false);
                    return;
                }
                throw new Error(`Domain resolution failed: ${response.status}`);
            }

            const data = await response.json();

            // Conserviamo eventuali campi extra (es. default_agent_id) esposti dal backend
            const finalConfig = { ...data, _isFallback: false, _fallbackReason: null, domain_name: data?.domain_name || domainName };

            setDomainConfig(finalConfig);
            setShouldShowCourtesyPage(false);

            sessionStorage.setItem(cacheKey, JSON.stringify({
                config: finalConfig,
                timestamp: Date.now()
            }));
        } catch (err) {
            setError(err?.message || String(err));
            setShouldShowCourtesyPage(true);
            setDomainConfig({
                ...FALLBACK_CONFIG,
                domain_name: domainName,
                _fallbackReason: err?.name === 'AbortError' ? 'network_error' : 'configuration_error'
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'it-IT';
        fetchDomainConfig(lang);
    }, [fetchDomainConfig]);

    // Costruzione tema MUI a partire dal visual theme del dominio
    const buildMuiTheme = useCallback(() => {
        const theme = domainConfig?.visual_theme || FALLBACK_VISUAL_THEME;
        return {
            palette: {
                primary: { main: theme.secondary_colors?.accent || '#1976d2' },
                secondary: { main: theme.secondary_colors?.link || '#dc004e' },
                background: {
                    default: theme.primary_colors?.background || '#f8f9fa',
                    paper: theme.primary_colors?.header || '#ffffff',
                },
                text: {
                    primary: theme.text_colors?.primary || '#212529',
                    secondary: theme.text_colors?.secondary || '#6c757d',
                },
            },
            typography: { fontFamily: theme.font_definitions?.primary_font || 'Inter, sans-serif' },
            components: {
                MuiButton: {
                    styleOverrides: {
                        root: { borderRadius: theme.component_styles?.button?.border_radius || '4px' },
                    },
                },
            },
        };
    }, [domainConfig]);

    const value = useMemo(() => {
        const config = domainConfig || FALLBACK_CONFIG;
        return {
            loading,
            error,
            config,
            buildMuiTheme,
            refreshConfig: fetchDomainConfig,
            shouldShowCourtesyPage,
            isFallback: !!config._isFallback,
            fallbackReason: config._fallbackReason || null,
            isConfigured: !config._isFallback && Boolean(config.project_id),
            currentDomain,
            // helper dedicato per l'Auth: estrae eventuale default agent se presente dal resolve
            defaultAgentId: config?.default_agent_id || null,
        };
    }, [loading, error, domainConfig, buildMuiTheme, fetchDomainConfig, shouldShowCourtesyPage]);

    return <DomainContext.Provider value={value}>{children}</DomainContext.Provider>;
};

// === HOOKS COMPATIBILI ===
export const useDomain = () => useContext(DomainContext);

export const useCourtesyPage = () => {
    const { shouldShowCourtesyPage, isFallback, fallbackReason, isConfigured, loading, refreshConfig } = useDomain();
    return {
        shouldShow: shouldShowCourtesyPage,
        isFallback,
        fallbackReason,
        isConfigured,
        isReady: !loading,
        retry: refreshConfig
    };
};

export const useVisualTheme = () => {
    const { config } = useDomain();
    return useMemo(() => config.visual_theme || FALLBACK_VISUAL_THEME, [config.visual_theme]);
};

export const useLocalization = () => {
    const { config, refreshConfig } = useDomain();
    const localization = useMemo(() => config.localization || FALLBACK_LOCALIZATION, [config.localization]);
    const getText = useCallback((key, fallback = null) => localization?.texts?.[key] || fallback || key, [localization]);
    return { getText, userLanguage: localization?.language_code, refreshConfig };
};

export const useBranding = () => {
    const { config } = useDomain();
    const visualTheme = config.visual_theme || FALLBACK_VISUAL_THEME;
    return useMemo(() => ({
        projectName: config.project_name,
        brandName: config.brand_name,
        organizationName: config.organization_name,
        logoUrl: visualTheme.logo_assets?.primary_light,
        primaryColor: visualTheme.secondary_colors?.accent,
    }), [config, visualTheme]);
};

export const useCurrentDomain = () => {
    const { currentDomain } = useDomain();
    return currentDomain;
};

export const useTextFields = () => {
    const { config } = useDomain();
    const visualTheme = config.visual_theme || FALLBACK_VISUAL_THEME;
    return useMemo(() => ({
        textHome1: visualTheme.text_home1 || '',
        textHome2: visualTheme.text_home2 || '',
        title1: visualTheme.title1 || '',
        title2: visualTheme.title2 || '',
        title3: visualTheme.title3 || '',
        descText1: visualTheme.desc_text1 || '',
        descText2: visualTheme.desc_text2 || '',
        descText3: visualTheme.desc_text3 || '',
        navbarText1: visualTheme.navbar_text1 || '',
        navbarText2: visualTheme.navbar_text2 || '',
        navbarText3: visualTheme.navbar_text3 || '',
        labelInputText: visualTheme.label_input_text || '',
        labelButtonText: visualTheme.label_button_text || '',
        hasText: (fieldName) => Boolean((visualTheme[fieldName] || '').toString().trim()),
        getText: (fieldName, fallback = '') => {
            const value = (visualTheme[fieldName] || '').toString().trim();
            return value || fallback;
        }
    }), [visualTheme]);
};

export const useHomeTexts = () => {
    const { config } = useDomain();
    const visualTheme = config.visual_theme || FALLBACK_VISUAL_THEME;
    return useMemo(() => ({
        heroTitle: (visualTheme.title1 || '').trim() || `Benvenuto su ${config.brand_name || config.project_name}`,
        heroSubtitle: (visualTheme.text_home1 || '').trim() || `Benvenuto su ${config.brand_name || config.project_name}`,
        heroDescription: (visualTheme.text_home2 || '').trim() || "La piattaforma innovativa per gestire i tuoi progetti con sicurezza e semplicità.",
        featuresTitle: (visualTheme.title2 || '').trim() || `Perché scegliere ${config.brand_name || config.project_name}?`,
        featuresDescription: (visualTheme.desc_text1 || '').trim() || "Scopri le funzionalità che rendono la nostra piattaforma unica e affidabile.",
        feature1Title: (visualTheme.desc_text2 || '').trim() || "Sicurezza Avanzata",
        feature2Title: (visualTheme.desc_text3 || '').trim() || "Performance Ottimali",
        feature3Title: (visualTheme.navbar_text1 || '').trim() || "Supporto 24/7",
        ctaTitle: (visualTheme.title3 || '').trim() || "Pronto a iniziare?",
        ctaDescription: (visualTheme.navbar_text2 || '').trim() || `Unisciti a migliaia di utenti che hanno già scelto ${config.brand_name || config.project_name}.`,
        buttonPrimaryText: (visualTheme.label_button_text || '').trim() || "Inizia Subito",
        buttonSecondaryText: (visualTheme.label_input_text || '').trim() || "Accedi",
        dashboardButtonText: (visualTheme.navbar_text3 || '').trim() || "Vai alla Dashboard"
    }), [config, visualTheme]);
};
